import React, { useState, useMemo, useEffect } from "react";
import { Row, Col, Card, CardHeader, CardBody, Label, Input } from "reactstrap";

const EpsInstructionsAccordion = ({
  instructions,
  onChange,
  activeColorStyle,
  isLightTheme,
  cardChromeStyle,
  inputStyle,
  isTheme2 = false,
  readOnly = false,
  requireOverride = false,
}) => {
  const fieldInputStyle = isTheme2
    ? inputStyle
    : { borderRadius: 0, ...inputStyle };
  const checkboxFieldStyle = isTheme2
    ? { fontSize: "14px", verticalAlign: "middle", marginTop: 0 }
    : { borderRadius: 0, fontSize: "14px", verticalAlign: "middle", marginTop: 0 };
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    // collapse all when instruction list changes in size
    setExpanded({});
  }, [instructions?.length]);

  const linkColor = useMemo(
    () => (isLightTheme ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.6)"),
    [isLightTheme]
  );

  const handleToggle = (idx) => setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));

  const updateInstruction = (updater) => {
    if (readOnly || !onChange) return;
    const next = [...(instructions || [])];
    updater(next);
    onChange(next);
  };

  const isOverridden = (instruction) => {
    if (!requireOverride) return true;
    if (instruction?.override) return true;
    const overrideSection = (instruction?.sections || []).find((s) => s?.type === 'checkbox' && typeof s?.label === 'string' && s.label.toLowerCase().includes('override portfolio defaults'));
    return !!overrideSection?.value;
  };

  if (!instructions || instructions.length === 0) {
    return (
      <div className="text-center py-4">
        <i className="tim-icons icon-alert-circle-exc text-muted" style={{ fontSize: '2rem' }} />
        <p className="text-muted mt-2">No payment instructions available.</p>
      </div>
    );
  }

  return (
    <div className="card-collapse" id="paymentInstructionsAccordion" role="tablist">
      {instructions.map((instruction, index) => (
        <Card key={index} className="card-plain" style={cardChromeStyle}>
          <CardHeader role="tab">
            <a
              aria-expanded={expanded[index] || false}
              href="#pablo"
              data-parent="#paymentInstructionsAccordion"
              data-toggle="collapse"
              onClick={(e) => {
                e.preventDefault();
                handleToggle(index);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                textDecoration: 'none',
                color: linkColor
              }}
            >


              <div className="d-flex align-items-center">
                <div className="mr-3" onClick={(e) => e.stopPropagation()}>
                  <Input
                    type="checkbox"
                    id={`instruction${index}`}
                    className="form-check-input"
                    style={{
                      marginTop: 0,
                      marginBottom: 0,
                      marginLeft: 0,
                      verticalAlign: 'middle',
                      backgroundColor: 'transparent',
                      position: 'relative',
                      top: '1px',
                      width: '18px',
                      height: '18px',
                      borderColor: activeColorStyle.borderColor,
                      accentColor: activeColorStyle.borderColor
                    }}
                    checked={!!instruction.enabled}
                    disabled={readOnly || (requireOverride && !isOverridden(instruction))}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      updateInstruction((next) => {
                        next[index].enabled = e.target.checked;
                      });
                    }}
                  />
                </div>
                <span className="font-weight-bold text-uppercase" style={{ fontSize: '0.8rem' }}>
                  {instruction.title}
                </span>
              </div>
              <i
                className={`tim-icons icon-minimal-down`}
                style={{ color: activeColorStyle.borderColor, transform: expanded[index] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 150ms ease' }}
              />
            </a>
          </CardHeader>

          {expanded[index] && instruction.sections && (
            <div className="collapse show" role="tabpanel">
              <CardBody style={{ paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
                {instruction.sections?.map((section, sectionIndex) => section.type === 'checkbox' && (
                  <div key={`checkbox-${sectionIndex}`} className="mb-3">
                    <div className="form-check d-flex align-items-center">
                      <Label className="form-check-label d-flex align-items-center" htmlFor={`section${index}_${sectionIndex}`}>
                        <Input
                          style={checkboxFieldStyle}
                          type="checkbox"
                          id={`section${index}_${sectionIndex}`}
                          className="form-check-input"
                          checked={!!section.value}
                          disabled={
                            // The override toggle itself must always be clickable (unless readOnly)
                            section?.key === 'override_portfolio_defaults'
                              ? readOnly
                              : (readOnly || (requireOverride && !isOverridden(instruction)))
                          }
                          onChange={(e) => {
                            updateInstruction((next) => {
                              next[index].sections[sectionIndex].value = e.target.checked;
                            });
                          }}
                        />
                        <span className="form-check-sign" />
                        {section.label}
                      </Label>
                    </div>
                  </div>
                ))}

                {instruction.sections?.map((section, sectionIndex) => section.type === 'alert' && (
                  <div key={`alert-${sectionIndex}`} className="mb-3">
                    <div className={`alert alert-${section.style || 'warning'}`} role="alert">
                      <i className="tim-icons icon-alert-circle-exc mr-2" />
                      {section.text}
                    </div>
                  </div>
                ))}

                <Row>
                  {instruction.sections?.map((section, sectionIndex) => section.type === 'group' && (
                    <Col key={`group-${sectionIndex}`} className="mb-4">
                      <Card className="h-100" style={{ ...cardChromeStyle, backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                        <CardHeader className="pb-2">
                          <h6 className="font-weight-bold mb-0 text-uppercase" style={{
                            color: activeColorStyle.borderColor,
                            fontSize: '0.85rem',
                            letterSpacing: '0.5px'
                          }}>
                            <i className="tim-icons icon-settings mr-2" />
                            {section.label}
                          </h6>
                          {section.help && (
                            <small className="text-muted d-block mt-2" style={{
                              fontStyle: 'italic',
                              fontSize: '0.8rem'
                            }}>
                              <i className="tim-icons icon-bulb-63 mr-1" />
                              {section.help}
                            </small>
                          )}
                        </CardHeader>
                        <CardBody className="pt-2">
                          <div>
                            {section.items && section.items.map((item, itemIndex) => (
                              <div key={itemIndex} className="mb-2">
                                {item.type === 'checkbox' && (
                                  <div className="form-check d-flex align-items-center">
                                    <Label className="form-check-label d-flex align-items-center" htmlFor={`item${index}_${sectionIndex}_${itemIndex}`}>
                                      <Input
                                        style={checkboxFieldStyle}
                                        type="checkbox"
                                        id={`item${index}_${sectionIndex}_${itemIndex}`}
                                        className="form-check-input"
                                        checked={!!item.value}
                                        disabled={readOnly || (requireOverride && !isOverridden(instruction))}
                                        onChange={(e) => {
                                          updateInstruction((next) => {
                                            next[index].sections[sectionIndex].items[itemIndex].value = e.target.checked;
                                          });
                                        }}
                                      />
                                      <span className="form-check-sign" />
                                      {item.label}
                                      {item.note && (
                                        <small className="text-muted ml-2" style={{ fontSize: '0.75rem' }}>
                                          ({item.note})
                                        </small>
                                      )}
                                    </Label>
                                  </div>
                                )}
                                {item.type === 'percentage' && (
                                  <div>
                                    <Label className="font-weight-bold" style={{ fontSize: '0.85rem' }}>
                                      {item.label}:
                                    </Label>
                                    <Input
                                      type="number"
                                      className="form-control"
                                      value={item.value || item.placeholder || "0"}
                                      disabled={readOnly || (requireOverride && !isOverridden(instruction))}
                                      onChange={(e) => {
                                        updateInstruction((next) => {
                                          next[index].sections[sectionIndex].items[itemIndex].value = e.target.value;
                                        });
                                      }}
                                      placeholder={item.placeholder || "0"}
                                      min="0"
                                      style={fieldInputStyle}
                                    />
                                  </div>
                                )}
                                {item.type === 'dollar' && (
                                  <div>
                                    <Label className="font-weight-bold" style={{ fontSize: '0.85rem' }}>
                                      {item.label}:
                                    </Label>
                                    <Input
                                      type="text"
                                      className="form-control"
                                      value={item.value || item.placeholder || "0.00"}
                                      disabled={readOnly || (requireOverride && !isOverridden(instruction))}
                                      onChange={(e) => {
                                        updateInstruction((next) => {
                                          next[index].sections[sectionIndex].items[itemIndex].value = e.target.value;
                                        });
                                      }}
                                      placeholder={item.placeholder || "0.00"}
                                      style={fieldInputStyle}
                                    />
                                  </div>
                                )}
                                {item.type === 'number' && (
                                  <div>
                                    <Label className="font-weight-bold" style={{ fontSize: '0.85rem' }}>
                                      {item.label}:
                                    </Label>
                                    <Input
                                      type="number"
                                      className="form-control"
                                      value={item.value || item.placeholder || "0"}
                                      disabled={readOnly || (requireOverride && !isOverridden(instruction))}
                                      onChange={(e) => {
                                        updateInstruction((next) => {
                                          next[index].sections[sectionIndex].items[itemIndex].value = e.target.value;
                                        });
                                      }}
                                      placeholder={item.placeholder || "0"}
                                      min="0"
                                      style={fieldInputStyle}
                                    />
                                    {item.note && (
                                      <small className="text-muted d-block mt-1" style={{ fontSize: '0.75rem' }}>
                                        <i className="tim-icons icon-bulb-63 mr-1" />
                                        {item.note}
                                      </small>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
};

export default EpsInstructionsAccordion;


